import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtPct, fmtValue } from "@/lib/format";
import { ErrorState, EmptyState, LoadingState, PageHeader, Panel } from "@/components/dash/ui";

const KPI_BY_DIM: Record<string, { kpi: string; label: string }[]> = {
  company: [
    { kpi: "odoo.sales_incl", label: "Odoo sales (incl. tax)" },
    { kpi: "odoo.sales_excl", label: "Odoo sales (excl. tax)" },
    { kpi: "odoo.tax", label: "Odoo tax" },
  ],
  branch: [
    { kpi: "repairs.completed", label: "Repairs completed" },
    { kpi: "repairs.new", label: "Repairs created" },
    { kpi: "rota.shifts_published", label: "Shifts published" },
  ],
};

export default function ComparisonPage({ dim }: { dim: "company" | "branch" }) {
  const opts = KPI_BY_DIM[dim];
  const [kpi, setKpi] = useState(opts[0].kpi);
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ["compare", dim, kpi, days], queryFn: () => dashApi.historyCompare(kpi, dim, days) });
  const unit = q.data?.unit ?? "count";
  const title = dim === "company" ? "Company Comparison" : "Branch Comparison";

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={`Compare ${dim} performance over the selected period vs the previous equal period.`} />
      <div className="flex flex-wrap items-center gap-2">
        <select value={kpi} onChange={(e) => setKpi(e.target.value)} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          {opts.map((o) => <option key={o.kpi} value={o.kpi}>{o.label}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          {[7, 30, 90].map((d) => <option key={d} value={d}>last {d} days</option>)}
        </select>
      </div>
      <Panel title={`By ${dim}`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.rows.length ? (
          <EmptyState>No {dim}-dimensioned history for this KPI.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">#</th>
                <th className="pb-2 font-medium">{dim}</th>
                <th className="pb-2 text-right font-medium">Current</th>
                <th className="pb-2 text-right font-medium">Previous</th>
                <th className="pb-2 text-right font-medium">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.rows.map((r, i) => {
                const up = (r.change_pct ?? 0) >= 0;
                return (
                  <tr key={r.name} className="text-slate-300">
                    <td className="py-2 font-semibold text-slate-500">{i + 1}</td>
                    <td className="py-2">{r.name}</td>
                    <td className="py-2 text-right tabular-nums text-white">{fmtValue(r.current, unit)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{fmtValue(r.previous, unit)}</td>
                    <td className={`py-2 text-right text-xs ${r.change_pct === null ? "text-slate-600" : up ? "text-emerald-400" : "text-rose-400"}`}>
                      {r.change_pct === null ? "—" : `${up ? "▲" : "▼"} ${fmtPct(r.change_pct)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
